import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    const hoverZoomQuery = '(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)';

    if ('hoverZoom' in this.dataset && window.matchMedia(hoverZoomQuery).matches) {
      this.addEventListener('pointermove', this.#handleHoverZoomMove, { signal });
      this.addEventListener('pointerleave', this.#resetHoverZoom, { signal });
    }

    if ('hoverSelect' in this.dataset && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      // pointermove, not pointerenter: thumbnails scrolling under a still pointer must not switch slides
      this.addEventListener('pointermove', this.#handleThumbnailHover, { signal });
    }
  }

  #controller = new AbortController();

  /** @type {HTMLImageElement | null} */
  #hoverZoomImage = null;

  /** @type {Element | null} */
  #hoveredThumbnail = null;

  /**
   * Shows a thumbnail's slide as soon as the pointer moves onto it, without a click.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleThumbnailHover = (event) => {
    const thumbnail =
      event.pointerType === 'mouse' && event.target instanceof Element
        ? event.target.closest('.slideshow-controls__thumbnail')
        : null;

    if (thumbnail === this.#hoveredThumbnail) return;
    this.#hoveredThumbnail = thumbnail;

    const index = thumbnail?.parentElement ? Array.from(thumbnail.parentElement.children).indexOf(thumbnail) : -1;
    if (index < 0) return;

    this.slideshow?.select(index, undefined, { animate: false });
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Magnifies the image under the pointer, keeping the hovered point in place.
   * The main (first) image is left alone; only the rest of the gallery zooms.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleHoverZoomMove = (event) => {
    const container =
      event.pointerType === 'mouse' && event.target instanceof Element
        ? event.target.closest('.product-media-container--image:not(.product-media-container--main)')
        : null;
    const image = container && !container.closest('dialog') ? container.querySelector('.product-media__image') : null;

    if (!container || !(image instanceof HTMLImageElement)) {
      this.#resetHoverZoom();
      return;
    }

    if (image !== this.#hoverZoomImage) {
      this.#resetHoverZoom();
      this.#hoverZoomImage = image;
      // Ask the browser for a source sharp enough for the scale in `.product-media__image--hover-zoom`
      image.sizes = `${Math.ceil(container.clientWidth * 1.5)}px`;
      image.classList.add('product-media__image--hover-zoom');
    }

    const rect = container.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    image.style.transformOrigin = `${x}% ${y}%`;
  };

  #resetHoverZoom = () => {
    this.#hoverZoomImage?.classList.remove('product-media__image--hover-zoom');
    this.#hoverZoomImage = null;
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
